/* eslint-disable no-unused-vars */
import { createContext } from 'react';
import { decorate, action, observable } from 'mobx';
import get from 'lodash/get';
import { PaymentAgent } from 'agents';
import adyenEncrypt from 'adyen-cse-js';
import { commonStore } from 'stores/common';
import { PAYMENT } from 'config';
import { PATHS } from 'config';

class PaymentStore {
    // when we need to show + tax close to final amount (us sales tax)
    plusTaxIsNeeded = false;
    paypal(data) {
        return PaymentAgent.paypal({
            ...data,
            instrument: data.instrument.toLowerCase(),
        });
    }

    encrypt(card) {
        const cse = adyenEncrypt.createEncryption(
            window.yconfig.adyenPublicKey,
            {},
        );

        if (card.expiry) {
            const [month, year] = card.expiry.split('/');
            card.expiryMonth = month;
            card.expiryYear = '20' + year;
        }

        return cse.encrypt({
            number: card.number.replace(/ /g, ''),
            cvc: card.cvv,
            holderName: card.name,
            expiryMonth: card.expiryMonth,
            expiryYear: card.expiryYear,
            generationtime: new Date(),
        });
    }
    setPlusTaxIsNeeded({ plan }) {
        const currency = (
            get(plan, 'core_plan_data.price.currency', '') ||
            get(plan, 'price.currency', '')
        ).toUpperCase();

        if (commonStore.isFromUSA && (currency === '$' || currency === 'USD')) {
            this.plusTaxIsNeeded = true;
        } else {
            this.plusTaxIsNeeded = false;
        }
    }

    updateCard(data) {
        const token = paymentStore.encrypt(data);

        if (!token) {
            return new Promise((_, reject) => {
                reject({
                    encrypt: true,
                });
            });
        }

        return PaymentAgent.updateCard({
            ptoken: token,
            email: data.email,
            ZIPCode: data.ZIPCode,
        }).then(commonStore.retrieveUserData);
    }

    paypalConfirm(response) {
        return PaymentAgent.paypalConfirm(response).then(
            commonStore.retrieveUserData,
        );
    }

    resubscribe(plan, extra = {}) {
        return PaymentAgent.resubscribe(plan, extra).then(
            commonStore.retrieveUserData,
        );
    }

    upgrade(plan, extra = {}, card = {}) {
        if (card && card.number && card.cvv) {
            const token = paymentStore.encrypt(card);

            if (!token) {
                throw new Error('encrypt');
            }

            extra.ptoken = token;
            extra.email = card.email;
            extra.zip_code = card.ZIPCode;
            extra.browser_info = {
                userAgent: window.navigator.userAgent,
                acceptHeader: '',
            };
        }

        return PaymentAgent.upgrade(plan, extra).then(
            commonStore.retrieveUserData,
        );
    }

    async card(data) {
        const token = paymentStore.encrypt(data);

        if (!token) {
            throw new Error('encrypt');
        }

        const result = await PaymentAgent.card({
            plan: data.plan,
            freetrial: data.free_trial,
            instrument: data.instrument.toLowerCase(),
            gateway: PAYMENT.WEB_GATEWAY,
            email: data.email || commonStore.user.email,
            coupon: data.coupon,
            ptoken: token,
            source: data.source,
            browser_info: {
                userAgent: window.navigator.userAgent,
                acceptHeader: '',
            },
            zip_code: data.ZIPCode,
            callbackUrl: data.callback_url || PATHS.subscribeWelcome,
            recaptcha_token: data.recaptcha_token,
        });

        await commonStore.retrieveUserData();

        return result;
    }

    async gift(data) {
        const token = this.encrypt(data);

        if (!token) {
            throw new Error('encrypt');
        }

        const purchaseData = {
            plan_id: data.plan,
            recipient_name: data.recipient_name,
            sender_name: data.sender_name,
            gift_message: data.gift_message,
            egift: data.egift,
            gateway: PAYMENT.WEB_GATEWAY,
            ptoken: token,
            browser_info: {
                userAgent: window.navigator.userAgent,
                acceptHeader: '',
            },
            zip_code: data.ZIPCode,
        };

        if (data.recipient_email) {
            purchaseData.recipient_email = data.recipient_email;
        }

        const result = await PaymentAgent.purchaseGift(purchaseData);

        await commonStore.retrieveGifts();

        return result;
    }
}

decorate(PaymentStore, {
    plusTaxIsNeeded: observable,
    setPlusTaxIsNeeded: action,
});

export const paymentStore = new PaymentStore();
export default createContext(paymentStore);
